import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Trash2, Plus, Download, Move } from 'lucide-react';
import { formatCurrency } from '../utils/currency';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import * as pdfjsLib from 'pdfjs-dist';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CREMONA_LOGO_DATA_URL } from '../utils/cremonaLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure pdf.js worker (served from /public)
if (typeof window !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
}

const COMPANY = {
  name: 'PT Cremona Structure Engineering',
  address: 'Citra Indah City - Blok AZ.03 No. 20',
  phone: '0812 7211 8361 / 0822 4983 2575 (WA)',
  email: 'Cremona.Structure@Gmail.com',
  banks: {
    freelance: { bank: 'BCA', account: '284.012.9058', holder: 'Johan Morrish Kabuaran' },
    company: { bank: 'BCA', account: '466.041.7031', holder: 'Cremona Structure Engineering' }
  }
};

const InvoiceCreator = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [invoiceHistory, setInvoiceHistory] = useState([]);  // all paid/partial invoices for "Paid info" calc
  const [selectedClientId, setSelectedClientId] = useState(searchParams.get('clientId') || '');
  const [invoiceMode, setInvoiceMode] = useState(''); // 'milestone' or 'project_list'
  const [financeBook, setFinanceBook] = useState('freelance');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(() => {
    // Default due date: invoice date + 14 days
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  });
  const [notes, setNotes] = useState('');

  // Editable client overrides (default from selected client but user can override)
  const [editableClientName, setEditableClientName] = useState('');
  const [editableClientCompany, setEditableClientCompany] = useState('');

  // Editable bank info (defaults from selected finance book, but user can override or pick custom)
  const [bankAccount, setBankAccount] = useState({ bank: 'BCA', account: '284.012.9058', holder: 'Johan Morrish Kabuaran' });
  const [bankSelection, setBankSelection] = useState('freelance'); // 'freelance' | 'company' | 'custom'

  // Signature image (base64)
  const [signatureFile, setSignatureFile] = useState(null);
  const [signatureFileName, setSignatureFileName] = useState('');
  const [signatureDims, setSignatureDims] = useState(null); // {w, h} natural dims

  // PDF Preview Dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDataUri, setPreviewDataUri] = useState('');
  // Signature position override on the PDF (in mm). null = auto-flow placement.
  const [signaturePos, setSignaturePos] = useState(null); // { x, y, w }

  // A4 page dimensions in mm (used by preview math)
  const PAGE_W = 210;
  const PAGE_H = 297;

  // Milestone mode
  const [milestoneItems, setMilestoneItems] = useState([]);
  const [milestoneProjectId, setMilestoneProjectId] = useState(''); // optional: tie milestone invoice to a single project

  // Project list mode
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [projectBillingData, setProjectBillingData] = useState([]);

  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => { fetchData(); }, []);

  // Sync editable client fields when selection changes
  useEffect(() => {
    const c = clients.find(c => c.id === selectedClientId);
    if (c) {
      setEditableClientName(c.name);
      setEditableClientCompany(c.company);
    }
  }, [selectedClientId, clients]);

  // Sync bank when book or selection changes
  useEffect(() => {
    if (bankSelection === 'freelance') setBankAccount({ ...COMPANY.banks.freelance });
    else if (bankSelection === 'company') setBankAccount({ ...COMPANY.banks.company });
    // 'custom' keeps current value
  }, [bankSelection]);

  const fetchData = async () => {
    try {
      const [c, p, inv] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/invoices?status=partial,paid`),
      ]);
      setClients(c.data);
      setProjects(p.data);
      setInvoiceHistory(inv.data || []);
    } catch { toast.error('Failed to load data'); }
  };

  // Returns { paidAmount, paidPercent, invoiceNumbers[] } for a given projectId
  // - paidAmount/Percent come from project.amountPaid (running total via transactions, always accurate)
  // - invoiceNumbers from invoiceHistory where the project appears (any status)
  const getProjectPaidInfo = (projectId) => {
    if (!projectId) return { paidAmount: 0, paidPercent: 0, invoiceNumbers: [] };
    const proj = projects.find(p => p.id === projectId);
    if (!proj) return { paidAmount: 0, paidPercent: 0, invoiceNumbers: [] };
    const paidAmount = proj.amountPaid || 0;
    const cv = proj.contractValue || 0;
    const paidPercent = cv > 0 ? (paidAmount / cv) * 100 : 0;
    // Find past invoices that include this project (milestone or project_list)
    const invoiceNumbers = invoiceHistory
      .filter(i => {
        if (i.mode === 'milestone' && i.milestoneProjectId === projectId) return true;
        if (i.mode === 'project_list' && Array.isArray(i.projectBillingData)) {
          return i.projectBillingData.some(p => p.projectId === projectId);
        }
        return false;
      })
      .map(i => i.invoiceNumber)
      .filter(Boolean);
    return { paidAmount, paidPercent, invoiceNumbers };
  };

  const client = clients.find(c => c.id === selectedClientId);
  const clientProjects = projects.filter(p => p.clientId === selectedClientId && (p.financeBook || 'freelance') === financeBook);

  const toggleProject = (pid) => setSelectedProjectIds(prev => prev.includes(pid) ? prev.filter(i => i !== pid) : [...prev, pid]);

  // Milestone mode helpers
  const addMilestoneItem = () => {
    setMilestoneItems([...milestoneItems, { id: crypto.randomUUID(), description: '', percent: 0, amount: 0 }]);
  };

  const updateMilestoneItem = (index, field, value) => {
    const updated = [...milestoneItems];
    // Compute the base (contract value or client-projects total) for both directions
    let base = 0;
    if (milestoneProjectId) {
      const proj = projects.find(p => p.id === milestoneProjectId);
      base = proj?.contractValue || 0;
    } else {
      base = clientProjects.reduce((s, p) => s + (p.contractValue || 0), 0);
    }
    if (field === 'percent') {
      updated[index].percent = parseFloat(value) || 0;
      updated[index].amount = Math.round(base * (updated[index].percent / 100));
    } else if (field === 'amount') {
      const amt = parseFloat(value) || 0;
      updated[index].amount = amt;
      // Derive matching percent so the invoice PDF shows a coherent value
      updated[index].percent = base > 0 ? (amt / base) * 100 : 0;
    } else {
      updated[index][field] = value;
    }
    setMilestoneItems(updated);
  };

  const removeMilestoneItem = (index) => {
    setMilestoneItems(milestoneItems.filter((_, i) => i !== index));
  };

  // Project list mode — generate billing data
  const generateProjectBilling = () => {
    if (selectedProjectIds.length === 0) { toast.error('Select at least one project'); return; }
    const data = selectedProjectIds.map(pid => {
      const proj = projects.find(p => p.id === pid);
      if (!proj) return null;
      return { projectId: pid, projectName: proj.name, contractValue: proj.contractValue, billingPercent: 0, amount: 0 };
    }).filter(Boolean);
    setProjectBillingData(data);
    setShowPreview(true);
  };

  const updateProjectBilling = (index, field, value) => {
    const updated = [...projectBillingData];
    const cv = updated[index].contractValue || 0;
    if (field === 'amount') {
      const amt = parseFloat(value) || 0;
      updated[index].amount = amt;
      // Derive the exact percent; keep full precision internally, format for display.
      updated[index].billingPercent = cv > 0 ? (amt / cv) * 100 : 0;
    } else {
      const pct = parseFloat(value) || 0;
      updated[index].billingPercent = pct;
      updated[index].amount = Math.round(cv * pct / 100);
    }
    setProjectBillingData(updated);
  };

  // Nicely format a percent number for display: strip meaningless zeros,
  // cap at 2 decimals, whole numbers show no decimal (e.g., 13, 13.5, 13.04).
  const fmtPct = (n) => {
    if (n === null || n === undefined || isNaN(n)) return '0';
    const rounded = Math.round(n * 100) / 100;
    if (Number.isInteger(rounded)) return String(rounded);
    return rounded.toFixed(2).replace(/\.?0+$/, '');
  };

  const fmtRp = (v) => `Rp ${(v || 0).toLocaleString('id-ID')}`;
  const bankInfo = bankAccount;

  // Signature file handler — reads the file, auto-crops whitespace, returns base64 + dims
  const handleSignatureFile = (file) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please drop a JPG or PNG image');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Signature must be under 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      const img = new Image();
      img.onload = () => {
        // Auto-crop whitespace / transparent pixels so signature fills the box
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
          let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0;
          let hasContent = false;
          for (let y = 0; y < canvas.height; y++) {
            for (let x = 0; x < canvas.width; x++) {
              const i = (y * canvas.width + x) * 4;
              const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
              // "content" = non-transparent and non-white pixel
              const isWhite = r > 235 && g > 235 && b > 235;
              if (a > 30 && !isWhite) {
                hasContent = true;
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
              }
            }
          }
          if (hasContent) {
            const padding = 4;
            minX = Math.max(0, minX - padding);
            minY = Math.max(0, minY - padding);
            maxX = Math.min(canvas.width - 1, maxX + padding);
            maxY = Math.min(canvas.height - 1, maxY + padding);
            const cropW = maxX - minX + 1;
            const cropH = maxY - minY + 1;
            const cropCanvas = document.createElement('canvas');
            cropCanvas.width = cropW;
            cropCanvas.height = cropH;
            cropCanvas.getContext('2d').drawImage(canvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH);
            const cropped = cropCanvas.toDataURL('image/png');
            setSignatureFile(cropped);
            setSignatureDims({ w: cropW, h: cropH });
          } else {
            setSignatureFile(dataUrl);
            setSignatureDims({ w: img.naturalWidth, h: img.naturalHeight });
          }
        } catch (err) {
          setSignatureFile(dataUrl);
          setSignatureDims({ w: img.naturalWidth, h: img.naturalHeight });
        }
      };
      img.src = dataUrl;
      setSignatureFileName(file.name);
      setSignaturePos(null); // reset manual position when new signature uploaded
      toast.success('Signature uploaded!');
    };
    reader.readAsDataURL(file);
  };

  const getInvoiceTotal = () => {
    if (invoiceMode === 'milestone') return milestoneItems.reduce((s, i) => s + (i.amount || 0), 0);
    return projectBillingData.reduce((s, p) => s + (p.amount || 0), 0);
  };

  const buildInvoiceDoc = (sigOverride) => {
    const total = getInvoiceTotal();

    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();
    let y = 12;

    // Cremona brand color (dark navy blue from logo)
    const BRAND = [27, 71, 117]; // #1B4775
    const BRAND_LIGHT = [73, 117, 163]; // accent shade

    // === HEADER: Big centered logo ===
    // Logo is ~4.64:1 aspect ratio (1532x330). Render at 90mm wide → ~19.4mm tall
    const logoW = 90;
    const logoH = 19.4;
    const logoX = (pw - logoW) / 2;
    try {
      doc.addImage(CREMONA_LOGO_DATA_URL, 'PNG', logoX, y, logoW, logoH);
    } catch (e) {
      // graceful fallback
    }
    y += logoH + 4;

    // Address / contact info — centered below logo
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(COMPANY.address, pw / 2, y, { align: 'center' }); y += 4;
    doc.text(`Tel: ${COMPANY.phone}  |  Email: ${COMPANY.email}`, pw / 2, y, { align: 'center' });
    y += 6;

    // Blue accent divider (thicker top stroke + thin bottom)
    doc.setDrawColor(...BRAND);
    doc.setLineWidth(1.2);
    doc.line(14, y, pw - 14, y);
    y += 1.5;
    doc.setLineWidth(0.3);
    doc.setDrawColor(...BRAND_LIGHT);
    doc.line(14, y, pw - 14, y);
    y += 8;

    // Reset to black for body
    doc.setTextColor(0, 0, 0);

    // Invoice Title — in brand color
    doc.setFontSize(20); doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND);
    doc.text('INVOICE', pw / 2, y, { align: 'center' }); y += 10;
    doc.setTextColor(0, 0, 0);

    // Format dates DD/MM/YYYY (Indonesian format)
    const fmtDate = (d) => {
      if (!d) return '-';
      const [year, month, day] = d.split('-');
      return `${day}/${month}/${year}`;
    };

    // Invoice details — issue & due dates on right column
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text('No. Invoice', 14, y); doc.text(`: ${invoiceNumber}`, 45, y);
    doc.text('Tanggal Terbit', pw - 95, y); doc.text(`: ${fmtDate(invoiceDate)}`, pw - 55, y);
    y += 6;

    // Project line (only for milestone mode when a project is picked)
    const milestoneProject = invoiceMode === 'milestone' && milestoneProjectId
      ? projects.find(p => p.id === milestoneProjectId)
      : null;
    if (milestoneProject) {
      doc.setFont('helvetica', 'bold');
      doc.text('Project', 14, y);
      doc.setFont('helvetica', 'normal');
      doc.text(`: ${milestoneProject.name}`, 45, y);
    }
    doc.setFont('helvetica', 'bold');
    doc.text('Tanggal Jatuh Tempo', pw - 95, y); doc.text(`: ${fmtDate(dueDate)}`, pw - 55, y);
    doc.setFont('helvetica', 'normal');
    y += 6;

    if (editableClientName) {
      doc.text('Kepada', 14, y); doc.text(`: ${editableClientName}`, 45, y); y += 5;
    }

    if (editableClientCompany) {
      doc.text(`  ${editableClientCompany}`, 45, y); y += 8;
    } else { y += 8; }

    // Table
    if (invoiceMode === 'milestone') {
      const head = [['No', 'Keterangan', 'Persen', 'Jumlah']];
      const body = milestoneItems.map((item, i) => [i + 1, item.description, `${fmtPct(item.percent || 0)}%`, fmtRp(item.amount)]);
      body.push([{ content: 'TOTAL', colSpan: 3, styles: { fontStyle: 'bold', halign: 'right', fillColor: [240, 246, 252], textColor: BRAND } }, { content: fmtRp(total), styles: { fontStyle: 'bold', fillColor: [240, 246, 252], textColor: BRAND } }]);

      autoTable(doc, {
        startY: y, head, body, theme: 'grid',
        styles: { fontSize: 10, cellPadding: 4 },
        headStyles: { fillColor: BRAND, textColor: [255, 255, 255], fontStyle: 'bold' },
        columnStyles: { 0: { cellWidth: 15 }, 2: { halign: 'center', cellWidth: 30 }, 3: { halign: 'right', cellWidth: 50 } },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 8;
    } else {
      const head = [['No', 'Project', 'Nilai Kontrak', 'Sudah Dibayar (%)', 'Persen Tagihan', 'Jumlah Tagihan']];
      const body = projectBillingData.map((p, i) => {
        const paidInfo = getProjectPaidInfo(p.projectId);
        return [
          i + 1,
          p.projectName,
          fmtRp(p.contractValue),
          `${Math.round(paidInfo.paidPercent)}%`,
          `${fmtPct(p.billingPercent)}%`,
          fmtRp(p.amount),
        ];
      });
      body.push([{ content: 'TOTAL', colSpan: 5, styles: { fontStyle: 'bold', halign: 'right', fillColor: [240, 246, 252], textColor: BRAND } }, { content: fmtRp(total), styles: { fontStyle: 'bold', fillColor: [240, 246, 252], textColor: BRAND } }]);

      autoTable(doc, {
        startY: y, head, body, theme: 'grid',
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: BRAND, textColor: [255, 255, 255], fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 10 },
          2: { halign: 'right' },
          3: { halign: 'center', cellWidth: 26 },
          4: { halign: 'center', cellWidth: 22 },
          5: { halign: 'right' },
        },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    // Milestone paid note (only when there are previous payments on the linked project)
    if (invoiceMode === 'milestone' && milestoneProjectId) {
      const paidInfo = getProjectPaidInfo(milestoneProjectId);
      // Skip note when 0% or fully paid (100%+)
      if (paidInfo.paidAmount > 0 && paidInfo.paidPercent < 100) {
        const invList = paidInfo.invoiceNumbers.length > 0
          ? ` - Invoice No. ${paidInfo.invoiceNumbers.join(', ')}`
          : '';
        const note = `*Project telah dibayar ${fmtRp(paidInfo.paidAmount)} (${Math.round(paidInfo.paidPercent)}%)${invList}`;
        doc.setFontSize(9); doc.setFont('helvetica', 'italic');
        doc.setTextColor(...BRAND);
        doc.text(note, 14, y, { maxWidth: pw - 28 });
        // If the note wraps, advance y by approximate line count
        const lines = doc.splitTextToSize(note, pw - 28);
        y += 5 * lines.length + 2;
        doc.setTextColor(0, 0, 0);
      }
    }

    // Notes
    if (notes) {
      doc.setFontSize(9); doc.setFont('helvetica', 'italic');
      doc.text(`Catatan: ${notes}`, 14, y); y += 8;
    }

    // Tax info
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text('*Non-PKP (PPN tidak dipungut)', 14, y); y += 10;

    // Payment info — with blue accent box
    const payBoxY = y;
    const payBoxH = 26;
    doc.setFillColor(240, 246, 252); // light blue tint
    doc.rect(14, payBoxY, pw - 28, payBoxH, 'F');
    // left blue accent stripe
    doc.setFillColor(...BRAND);
    doc.rect(14, payBoxY, 2, payBoxH, 'F');
    
    doc.setFontSize(10); doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND);
    doc.text('Pembayaran dapat ditransfer ke:', 20, y + 6); y += 11;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(`Bank: ${bankInfo.bank}`, 20, y); y += 5;
    doc.text(`No. Rekening: ${bankInfo.account}`, 20, y); y += 5;
    doc.text(`Atas Nama: ${bankInfo.holder}`, 20, y);
    y = payBoxY + payBoxH + 12;

    // Signature block
    doc.setFont('helvetica', 'normal');
    const sigBlockX = pw - 80; // right side
    doc.text('Hormat kami,', sigBlockX, y); y += 2;

    // Use sigOverride if provided, else use signaturePos state, else auto-flow
    const effectiveSigPos = sigOverride !== undefined ? sigOverride : signaturePos;

    // Embed signature image — REALLY BIG, overlapping the name
    if (signatureFile && signatureDims) {
      const ratio = signatureDims.w / signatureDims.h;
      let sigX, sigY, sigW, sigH;

      if (effectiveSigPos) {
        // Manual placement
        sigX = effectiveSigPos.x;
        sigY = effectiveSigPos.y;
        sigW = effectiveSigPos.w;
        sigH = sigW / ratio;
      } else {
        // Auto placement
        const maxH = 50, maxW = 95;
        if (ratio >= maxW / maxH) {
          sigW = maxW; sigH = sigW / ratio;
        } else {
          sigH = maxH; sigW = sigH * ratio;
        }
        sigX = sigBlockX - 6;
        sigY = y;
      }

      try {
        const fmt = signatureFile.startsWith('data:image/png') ? 'PNG' : 'JPEG';
        doc.addImage(signatureFile, fmt, sigX, sigY, sigW, sigH);
      } catch (e) { /* graceful */ }

      // Advance y past signature for name placement
      // If manual override, position name AT a fixed offset from page (not from sig)
      // If auto, name placement overlaps the sig by 8mm
      if (effectiveSigPos) {
        // For manual: place name just below the signature with -8mm overlap (still touching)
        y = sigY + sigH - 8;
      } else {
        y += sigH - 8;
      }
    } else {
      y += 25;
    }

    doc.setFont('helvetica', 'bold');
    doc.text('Johan Morrish Kabuaran', sigBlockX, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text('Principal Engineer', sigBlockX, y);

    return doc;
  };

  // Default signature box (in mm) — used as starting point when user opens preview
  const computeDefaultSigPos = () => {
    if (!signatureDims) return null;
    const ratio = signatureDims.w / signatureDims.h;
    const maxH = 50, maxW = 95;
    let sigW, sigH;
    if (ratio >= maxW / maxH) { sigW = maxW; sigH = sigW / ratio; }
    else { sigH = maxH; sigW = sigH * ratio; }
    // Default to lower-right area
    return { x: PAGE_W - 80 - 6, y: PAGE_H - 60, w: sigW };
  };

  const handleGenerateClick = () => {
    if (!invoiceNumber) { toast.error('Please enter invoice number'); return; }
    const total = getInvoiceTotal();
    if (total <= 0) { toast.error('Invoice total must be greater than 0'); return; }

    // Initialize signature override only on first preview
    if (!signaturePos && signatureFile) {
      setSignaturePos(computeDefaultSigPos());
    }

    const doc = buildInvoiceDoc();
    // Use blob URL — works reliably across browsers (data URIs can fail for large PDFs)
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    setPreviewDataUri(prev => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return url;
    });
    setPreviewOpen(true);
  };

  const refreshPreview = (newPos) => {
    const doc = buildInvoiceDoc(newPos);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    setPreviewDataUri(prev => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return url;
    });
  };

  const downloadFinalPDF = async () => {
    const doc = buildInvoiceDoc();
    const safeClient = (editableClientName || 'Client').replace(/[/\\]/g, '-');

    // Save invoice to DB as postponed (only on first download)
    let savedInvoiceId = null;
    try {
      // Snapshot the paid info into the saved invoice so re-downloads show identical numbers.
      // - For project_list: enrich each row with paidPercent/paidAmount/invoiceNumbers
      // - For milestone: pre-compute the paid note string
      const enrichedProjectBilling = invoiceMode === 'project_list'
        ? projectBillingData.map(p => {
            const pi = getProjectPaidInfo(p.projectId);
            return {
              ...p,
              paidPercent: pi.paidPercent,
              paidAmount: pi.paidAmount,
              paidInvoiceNumbers: pi.invoiceNumbers,
            };
          })
        : [];

      let milestonePaidNote = null;
      if (invoiceMode === 'milestone' && milestoneProjectId) {
        const pi = getProjectPaidInfo(milestoneProjectId);
        if (pi.paidAmount > 0 && pi.paidPercent < 100) {
          const fmtRp = (v) => `Rp ${(v || 0).toLocaleString('id-ID')}`;
          const invList = pi.invoiceNumbers.length > 0
            ? ` - Invoice No. ${pi.invoiceNumbers.join(', ')}`
            : '';
          milestonePaidNote = `*Project telah dibayar ${fmtRp(pi.paidAmount)} (${Math.round(pi.paidPercent)}%)${invList}`;
        }
      }

      const payload = {
        invoiceNumber,
        clientId: selectedClientId || '',
        clientName: editableClientName,
        clientCompany: editableClientCompany,
        financeBook,
        mode: invoiceMode,
        milestoneItems: invoiceMode === 'milestone' ? milestoneItems : [],
        milestoneProjectId: invoiceMode === 'milestone' ? milestoneProjectId : null,
        milestoneProjectName: invoiceMode === 'milestone' && milestoneProjectId
          ? (projects.find(p => p.id === milestoneProjectId)?.name || null)
          : null,
        milestonePaidNote,
        projectBillingData: enrichedProjectBilling,
        totalAmount: getInvoiceTotal(),
        issueDate: invoiceDate,
        dueDate,
        bankAccount,
        notes,
        signatureFile,
        signatureDims,
        signaturePos,
      };
      const resp = await axios.post(`${API}/invoices`, payload);
      savedInvoiceId = resp.data.id;
    } catch (err) {
      console.error('Failed to save invoice to DB', err);
      toast.error('Invoice saved as PDF only — failed to save for tracking');
    }

    doc.save(`Invoice_${invoiceNumber.replace(/[/\\]/g, '-')}_${safeClient}.pdf`);
    setPreviewOpen(false);
    if (savedInvoiceId) {
      toast.success('Invoice downloaded & saved as postponed!');
    } else {
      toast.success('Invoice PDF downloaded!');
    }
  };

  return (
    <div className="min-h-screen p-6" data-testid="invoice-page">
      <div className="max-w-5xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/clients')} className="mb-4" data-testid="back-to-clients">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Clients
        </Button>
        <div className="mb-6 relative">
          <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded absolute top-0 right-0">A.4.2</span>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Work Sans, sans-serif' }}>Invoice Creator</h1>
          <p className="text-slate-600">Generate invoice for your client</p>
        </div>

        {/* Step 1: Client + Settings */}
        <Card className="mb-6">
          <CardHeader><CardTitle>1. Invoice Settings</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Client (pre-fill template)</Label>
                <Select value={selectedClientId} onValueChange={(v) => { setSelectedClientId(v); setSelectedProjectIds([]); setShowPreview(false); }}>
                  <SelectTrigger data-testid="invoice-client-select"><SelectValue placeholder="Select client to pre-fill" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name} - {c.company}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1 italic">Selecting a client pre-fills the editable fields below.</p>
              </div>
              <div>
                <Label>Finance Book (filters projects)</Label>
                <Select value={financeBook} onValueChange={(v) => { setFinanceBook(v); setSelectedProjectIds([]); setShowPreview(false); }}>
                  <SelectTrigger data-testid="invoice-book-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="freelance">Freelance</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Editable Client Name + Company on the invoice */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Client Name (Kepada)</Label>
                <Input
                  value={editableClientName}
                  onChange={e => setEditableClientName(e.target.value)}
                  placeholder="e.g., Bpk. Urip Pastintoro"
                  data-testid="invoice-client-name"
                />
              </div>
              <div>
                <Label>Client Company</Label>
                <Input
                  value={editableClientCompany}
                  onChange={e => setEditableClientCompany(e.target.value)}
                  placeholder="e.g., PT. Quira Archipro Parama"
                  data-testid="invoice-client-company"
                />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Invoice Number</Label>
                <Input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="INV-2026-001" data-testid="invoice-number" />
              </div>
              <div>
                <Label>Tanggal Terbit</Label>
                <Input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} data-testid="invoice-date" />
              </div>
              <div>
                <Label>Tanggal Jatuh Tempo</Label>
                <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} data-testid="invoice-due-date" />
              </div>
              <div>
                <Label>Invoice Mode</Label>
                <Select value={invoiceMode} onValueChange={(v) => { setInvoiceMode(v); setShowPreview(false); }}>
                  <SelectTrigger data-testid="invoice-mode-select"><SelectValue placeholder="Select mode" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="milestone">Milestone Based (DP, Schematic, DED...)</SelectItem>
                    <SelectItem value="project_list">Project List Based (% per project)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Bank Account Section */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Bank Account on Invoice</Label>
              <div className="grid grid-cols-4 gap-3 mt-2">
                <div>
                  <Label className="text-xs">Choose</Label>
                  <Select value={bankSelection} onValueChange={setBankSelection}>
                    <SelectTrigger data-testid="bank-selection"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="freelance">Freelance — BCA {COMPANY.banks.freelance.account}</SelectItem>
                      <SelectItem value="company">Company — BCA {COMPANY.banks.company.account}</SelectItem>
                      <SelectItem value="custom">Custom (enter manually)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Bank</Label>
                  <Input
                    value={bankAccount.bank}
                    onChange={e => { setBankAccount({ ...bankAccount, bank: e.target.value }); setBankSelection('custom'); }}
                    placeholder="BCA"
                    data-testid="bank-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Account Number</Label>
                  <Input
                    value={bankAccount.account}
                    onChange={e => { setBankAccount({ ...bankAccount, account: e.target.value }); setBankSelection('custom'); }}
                    placeholder="284.012.9058"
                    data-testid="bank-account"
                  />
                </div>
                <div>
                  <Label className="text-xs">Account Holder</Label>
                  <Input
                    value={bankAccount.holder}
                    onChange={e => { setBankAccount({ ...bankAccount, holder: e.target.value }); setBankSelection('custom'); }}
                    placeholder="Johan Morrish Kabuaran"
                    data-testid="bank-holder"
                  />
                </div>
              </div>
            </div>

            {/* Signature Drop Zone */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Signature (Optional)</Label>
              <p className="text-xs text-slate-500 mb-2">Drop a JPG or PNG of your signature — auto-sized for best fit on the invoice.</p>
              {signatureFile ? (
                <div className="flex items-center gap-3 p-3 border-2 border-blue-200 bg-blue-50 rounded-lg">
                  <img src={signatureFile} alt="signature" className="max-h-20 bg-white p-1 border rounded" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900">{signatureFileName}</p>
                    {signatureDims && (
                      <p className="text-xs text-blue-700">{signatureDims.w}×{signatureDims.h}px</p>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setSignatureFile(null); setSignatureFileName(''); setSignatureDims(null); setSignaturePos(null); }} data-testid="remove-signature-btn">
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ) : (
                <div
                  className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors cursor-pointer"
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-blue-500', 'bg-blue-50'); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50'); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                    const file = e.dataTransfer.files?.[0];
                    if (file) handleSignatureFile(file);
                  }}
                  onClick={() => document.getElementById('signature-input')?.click()}
                  data-testid="signature-dropzone"
                >
                  <Plus className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-600">Drop signature image here or click to browse</p>
                  <p className="text-xs text-slate-400 mt-1">JPG or PNG · Max 2MB</p>
                  <input
                    id="signature-input"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => { if (e.target.files?.[0]) handleSignatureFile(e.target.files[0]); }}
                  />
                </div>
              )}
            </div>

            <div>
              <Label>Notes / Catatan (Optional)</Label>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Additional notes for the invoice..." className="min-h-[40px] text-sm" />
            </div>
          </CardContent>
        </Card>

        {/* Step 2: Milestone Mode */}
        {invoiceMode === 'milestone' && (
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>2. Invoice Items (Milestone)</CardTitle>
                <Button size="sm" variant="outline" onClick={addMilestoneItem}><Plus className="w-3 h-3 mr-1" /> Add Item</Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Single project selector — appears on the invoice and is the % base */}
              <div className="mb-4">
                <Label>Project (Optional — for single-project invoice)</Label>
                <Select value={milestoneProjectId || 'none'} onValueChange={(v) => setMilestoneProjectId(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="milestone-project-select"><SelectValue placeholder="Select a project..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none"><span className="italic text-slate-500">None (no project line on invoice)</span></SelectItem>
                    {(selectedClientId ? clientProjects : projects.filter(p => p.financeBook === financeBook)).map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name} — {formatCurrency(p.contractValue)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {milestoneProjectId && (() => {
                  const pi = getProjectPaidInfo(milestoneProjectId);
                  return (
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-blue-600">
                        ✓ Project will appear on the invoice. % calculations use this project's contract value as the base.
                      </p>
                      {pi.paidAmount > 0 && pi.paidPercent < 100 && (
                        <p
                          className="text-xs px-2 py-1 bg-amber-50 border border-amber-200 text-amber-900 rounded inline-flex items-center gap-1"
                          data-testid="milestone-paid-info"
                        >
                          <span className="font-bold">Sudah Dibayar:</span>
                          {formatCurrency(pi.paidAmount)} ({Math.round(pi.paidPercent)}%)
                          {pi.invoiceNumbers.length > 0 && (
                            <span className="text-amber-700 ml-1">— {pi.invoiceNumbers.join(', ')}</span>
                          )}
                          <span className="ml-1 text-slate-500 italic">(will be added as a note on the PDF)</span>
                        </p>
                      )}
                      {pi.paidPercent >= 100 && (
                        <p className="text-xs px-2 py-1 bg-green-50 border border-green-200 text-green-900 rounded inline-block">
                          ✓ Project fully paid (100%)
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>

              {milestoneItems.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-4">Add invoice items (e.g., "DP 30% - Project A", "Pembayaran DED")</p>
              ) : (
                <div className="space-y-3">
                  {milestoneProjectId ? (
                    <p className="text-xs text-slate-500">
                      Base: {formatCurrency(projects.find(p => p.id === milestoneProjectId)?.contractValue || 0)} (selected project's contract value)
                    </p>
                  ) : selectedClientId ? (
                    <p className="text-xs text-slate-500">Base: {formatCurrency(clientProjects.reduce((s, p) => s + (p.contractValue || 0), 0))} (total {financeBook} projects for this client)</p>
                  ) : null}
                  {milestoneItems.map((item, i) => (
                    <div key={item.id} className="flex gap-3 items-center">
                      <span className="text-sm text-slate-400 w-6">{i + 1}.</span>
                      <Input value={item.description} onChange={e => updateMilestoneItem(i, 'description', e.target.value)} placeholder="e.g., DP 30% Schematic" className="flex-1" />
                      <div className="flex items-center gap-1">
                        <Input type="number" step="0.01" value={item.percent ? fmtPct(item.percent) : ''} onChange={e => updateMilestoneItem(i, 'percent', e.target.value)} placeholder="%" className="w-16 text-center" min="0" max="100" data-testid={`milestone-percent-${i}`} />
                        <span className="text-xs text-slate-400">%</span>
                      </div>
                      <Input
                        type="number"
                        value={item.amount || ''}
                        onChange={e => updateMilestoneItem(i, 'amount', e.target.value)}
                        placeholder="Amount"
                        className="w-40 text-right"
                        data-testid={`milestone-amount-${i}`}
                      />
                      <Button variant="ghost" size="sm" onClick={() => removeMilestoneItem(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-3 border-t font-bold">
                    <span>Total Invoice</span>
                    <span className="text-lg">{formatCurrency(milestoneItems.reduce((s, i) => s + (i.amount || 0), 0))}</span>
                  </div>
                  <Button onClick={handleGenerateClick} className="w-full bg-slate-800 hover:bg-slate-900 mt-4" data-testid="generate-invoice-btn">
                    <FileText className="w-4 h-4 mr-2" /> Generate Invoice PDF
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 2: Project List Mode */}
        {invoiceMode === 'project_list' && selectedClientId && (
          <Card className="mb-6">
            <CardHeader><CardTitle>2. Select Projects</CardTitle></CardHeader>
            <CardContent>
              {clientProjects.length === 0 ? (
                <p className="text-slate-500 text-sm">No {financeBook} projects for this client.</p>
              ) : (
                <div className="space-y-2">
                  {clientProjects.map(p => (
                    <div key={p.id} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50">
                      <Checkbox checked={selectedProjectIds.includes(p.id)} onCheckedChange={() => toggleProject(p.id)} />
                      <span className="font-medium">{p.name}</span>
                      <span className="text-sm text-slate-500">{formatCurrency(p.contractValue)}</span>
                    </div>
                  ))}
                  <Button onClick={generateProjectBilling} className="mt-4 bg-slate-800 hover:bg-slate-900" data-testid="preview-invoice-btn">
                    Continue → Set Billing Amounts
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Project List Preview */}
        {invoiceMode === 'project_list' && showPreview && projectBillingData.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>3. Set Billing Percentage</CardTitle>
                <Button onClick={handleGenerateClick} className="bg-slate-800 hover:bg-slate-900" data-testid="generate-invoice-btn">
                  <FileText className="w-4 h-4 mr-2" /> Generate Invoice PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">No</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead className="text-right">Nilai Kontrak</TableHead>
                    <TableHead className="text-center">Sudah Dibayar (%)</TableHead>
                    <TableHead className="text-center">Persen Tagihan (%)</TableHead>
                    <TableHead className="text-right">Jumlah Tagihan (Rp)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projectBillingData.map((p, i) => {
                    const pi = getProjectPaidInfo(p.projectId);
                    return (
                      <TableRow key={p.projectId}>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell className="font-medium">{p.projectName}</TableCell>
                        <TableCell className="text-right">{formatCurrency(p.contractValue)}</TableCell>
                        <TableCell className="text-center" title={`${formatCurrency(pi.paidAmount)} of ${formatCurrency(p.contractValue)}`}>
                          <span className={pi.paidPercent >= 100 ? 'font-bold text-green-600' : pi.paidPercent > 0 ? 'font-medium text-amber-700' : 'text-slate-400'}>
                            {Math.round(pi.paidPercent)}%
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Input
                            type="number"
                            step="0.01"
                            value={p.billingPercent ? fmtPct(p.billingPercent) : ''}
                            onChange={e => updateProjectBilling(i, 'percent', e.target.value)}
                            className="w-20 h-8 text-center text-sm mx-auto"
                            min="0" max="100"
                            data-testid={`billing-percent-${i}`}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            value={p.amount || ''}
                            onChange={e => updateProjectBilling(i, 'amount', e.target.value)}
                            className="w-32 h-8 text-right text-sm ml-auto"
                            min="0"
                            data-testid={`billing-amount-${i}`}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow className="bg-slate-100 font-bold border-t-2">
                    <TableCell colSpan={5} className="text-right">TOTAL</TableCell>
                    <TableCell className="text-right">{formatCurrency(projectBillingData.reduce((s, p) => s + (p.amount || 0), 0))}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Button
                onClick={handleGenerateClick}
                className="w-full bg-slate-800 hover:bg-slate-900 mt-4"
                data-testid="generate-invoice-btn-bottom"
              >
                <FileText className="w-4 h-4 mr-2" /> Generate Invoice PDF
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Invoice Preview Info */}
        {invoiceMode && (
          <Card className="mb-6 bg-slate-50">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 mb-2">Invoice will include:</p>
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                <span>Company: {COMPANY.name}</span>
                <span>Bank: {bankInfo.bank} - {bankInfo.account}</span>
                <span>Client: {editableClientName || '—'}{editableClientCompany ? ` - ${editableClientCompany}` : ''}</span>
                <span>Account: {bankInfo.holder}</span>
                <span>Tax: Non-PKP (PPN tidak dipungut)</span>
                <span>Signed by: Johan Morrish Kabuaran {signatureFile && '✓ (signature uploaded)'}</span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* PDF PREVIEW DIALOG with draggable + resizable signature */}
      <PdfPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        previewDataUri={previewDataUri}
        signatureFile={signatureFile}
        signatureDims={signatureDims}
        signaturePos={signaturePos}
        setSignaturePos={setSignaturePos}
        refreshPreview={refreshPreview}
        onDownload={downloadFinalPDF}
        pageW={PAGE_W}
        pageH={PAGE_H}
      />
    </div>
  );
};

// =====================
// PDF Preview Dialog
// =====================
const PdfPreviewDialog = ({
  open, onOpenChange, previewDataUri, signatureFile, signatureDims,
  signaturePos, setSignaturePos, refreshPreview, onDownload, pageW, pageH
}) => {
  const containerRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const [containerSize, setContainerSize] = React.useState({ w: 0, h: 0 });

  const PAGE_WIDTH_PX = 720;

  // Measure container so we can convert mm <-> px
  React.useEffect(() => {
    if (!open) return;
    let rafId;
    let ro;
    const tryMeasure = () => {
      if (!containerRef.current) {
        rafId = requestAnimationFrame(tryMeasure);
        return;
      }
      const update = () => {
        const r = containerRef.current.getBoundingClientRect();
        setContainerSize({ w: r.width, h: r.height });
      };
      update();
      ro = new ResizeObserver(update);
      ro.observe(containerRef.current);
    };
    tryMeasure();
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (ro) ro.disconnect();
    };
  }, [open]);

  // Render PDF blob to canvas with pdf.js
  React.useEffect(() => {
    if (!open || !previewDataUri) return;
    let cancelled = false;
    let rafId;

    const tryRender = async () => {
      const canvas = canvasRef.current;
      if (!canvas) {
        // Canvas not mounted yet — retry on next frame
        rafId = requestAnimationFrame(tryRender);
        return;
      }
      try {
        const resp = await fetch(previewDataUri);
        const arrBuf = await resp.arrayBuffer();
        if (cancelled) return;
        const loadingTask = pdfjsLib.getDocument({ data: arrBuf });
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1 });
        const scale = PAGE_WIDTH_PX / viewport.width;
        const scaledViewport = page.getViewport({ scale });
        const dpr = window.devicePixelRatio || 1;
        canvas.width = scaledViewport.width * dpr;
        canvas.height = scaledViewport.height * dpr;
        canvas.style.width = scaledViewport.width + 'px';
        canvas.style.height = scaledViewport.height + 'px';
        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);
        await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
      } catch (err) {
        console.error('[PdfPreview] render error', err);
      }
    };

    tryRender();

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [open, previewDataUri]);

  if (!open) return null;

  const ratioImg = signatureDims ? signatureDims.w / signatureDims.h : 1;
  const pxPerMm = containerSize.w / pageW;

  // Mouse-based drag/resize handlers
  const startDrag = (e, mode) => {
    if (!signaturePos) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startPos = { ...signaturePos };

    const onMove = (ev) => {
      const dxMm = (ev.clientX - startX) / pxPerMm;
      const dyMm = (ev.clientY - startY) / pxPerMm;
      let newPos = { ...startPos };
      if (mode === 'drag') {
        newPos.x = Math.max(0, Math.min(pageW - 5, startPos.x + dxMm));
        newPos.y = Math.max(0, Math.min(pageH - 5, startPos.y + dyMm));
      } else if (mode === 'resize') {
        newPos.w = Math.max(20, Math.min(pageW - newPos.x - 5, startPos.w + dxMm));
      }
      setSignaturePos(newPos);
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      // Refresh PDF preview with the latest position
      setTimeout(() => {
        setSignaturePos(prev => {
          refreshPreview(prev);
          return prev;
        });
      }, 0);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Compute overlay box style (in px) from signaturePos (in mm)
  const sigBoxStyle = signaturePos && signatureFile && pxPerMm > 0 ? {
    left: signaturePos.x * pxPerMm,
    top: signaturePos.y * pxPerMm,
    width: signaturePos.w * pxPerMm,
    height: (signaturePos.w / ratioImg) * pxPerMm,
  } : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-hidden flex flex-col" data-testid="pdf-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" /> Invoice Preview
            {signatureFile && (
              <span className="text-xs font-normal text-blue-600 ml-2">
                <Move className="w-3 h-3 inline mr-1" /> Drag the signature to reposition · Drag the corner to resize
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto bg-slate-200 p-4 rounded">
          <div
            className="mx-auto bg-white shadow-lg"
            style={{ width: PAGE_WIDTH_PX, position: 'relative' }}
            ref={containerRef}
          >
            <canvas ref={canvasRef} style={{ display: 'block', width: '100%' }} data-testid="pdf-preview-canvas" />

            {/* Draggable signature overlay */}
            {sigBoxStyle && (
              <div
                className="absolute border-2 border-dashed border-blue-500 cursor-move group hover:border-blue-700"
                style={{ ...sigBoxStyle, background: 'rgba(59, 130, 246, 0.05)' }}
                onMouseDown={(e) => startDrag(e, 'drag')}
                data-testid="signature-overlay"
              >
                {/* tiny hint label */}
                <div className="absolute -top-5 left-0 text-[10px] font-mono text-blue-700 bg-blue-50 px-1 rounded whitespace-nowrap">
                  <Move className="w-2.5 h-2.5 inline mr-0.5" />
                  {Math.round(signaturePos.w)}mm × {Math.round(signaturePos.w / ratioImg)}mm
                </div>
                {/* resize handle */}
                <div
                  className="absolute -bottom-1 -right-1 w-4 h-4 bg-blue-600 rounded cursor-nwse-resize hover:bg-blue-800"
                  onMouseDown={(e) => startDrag(e, 'resize')}
                  data-testid="signature-resize-handle"
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pt-3 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="preview-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Edit
          </Button>
          <div className="flex items-center gap-2">
            {signatureFile && signaturePos && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSignaturePos(null);
                  setTimeout(() => refreshPreview(null), 0);
                }}
                data-testid="reset-signature-pos"
              >
                Reset Signature Position
              </Button>
            )}
            <Button onClick={onDownload} className="bg-blue-700 hover:bg-blue-800" data-testid="download-pdf-btn">
              <Download className="w-4 h-4 mr-2" /> Save & Download PDF
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceCreator;
